import type { ReactElement } from "react";
import type { TaskWithFlags } from "@desk/contracts";
import type { RowChipEditors } from "@/concepts/types";
import { DatePicker } from "@/components/ui/date-picker";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTaskActions } from "@/data/actions";
import { useHierarchy } from "@/data/hooks";
import { LabelsPopover } from "./detail-parts";
import { PriorityRadioItems, StatusRadioItems } from "./TaskMenu";

// Each picker keeps clicks and keys inside itself so the row doesn't select, open, or run shortcuts.
const stop = { onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(), onKeyDown: (e: { stopPropagation: () => void }) => e.stopPropagation() };

function PriorityPicker({ task, trigger }: { task: TaskWithFlags; trigger: ReactElement }) {
  const a = useTaskActions();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" {...stop}>
        <PriorityRadioItems value={task.priority} onChange={(p) => void a.setPriority(task, p)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusPicker({ task, trigger }: { task: TaskWithFlags; trigger: ReactElement }) {
  const a = useTaskActions();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" {...stop}>
        <StatusRadioItems value={task.status} onChange={(s) => void a.setStatus(task, s)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DeadlinePicker({ task, trigger }: { task: TaskWithFlags; trigger: ReactElement }) {
  const a = useTaskActions();
  const h = useHierarchy();
  return (
    <span className="contents" {...stop}>
      <DatePicker value={task.deadline} today={h.today} align="end" clearLabel="No deadline" onChange={(d) => void a.update(task, { deadline: d })}>
        {trigger}
      </DatePicker>
    </span>
  );
}

/** Row chip pickers for a task: priority, status, deadline, and labels open in place. */
export function rowChipEditors(task: TaskWithFlags): RowChipEditors {
  return {
    priority: (trigger) => <PriorityPicker task={task} trigger={trigger} />,
    status: (trigger) => <StatusPicker task={task} trigger={trigger} />,
    deadline: (trigger) => <DeadlinePicker task={task} trigger={trigger} />,
    labels: (trigger) => <LabelsPopover task={task}>{trigger}</LabelsPopover>,
  };
}
